import { SessionDto, ServiceResponse } from "@se/platform";
import { ILoggingService } from "@se/designsystem-core";
import { IMessagingService } from "@se/designsystem-core";
import { ISessionService, ISessionServiceId } from "@se/designsystem-core";
import { SearchSupportComponent } from "./searchSupportComponent";
import { inject } from "inversify";
import { first } from "rxjs/operators";

class SearchSupportAutoComplete extends SearchSupportComponent {
    private resultsOutput: HTMLElement | null;
    private resultTemplate: HTMLTemplateElement;
    private results: string[];
    private resultsOutputItems: HTMLElement[];
    private autoCompleteResultsAnnouncement: HTMLElement;

    private onSearchInputFocus: EventListener;
    private onSearchInputChange: EventListener;
    private onSearchInputKeyDown: EventListener;
    private onResultsOutputKeyDown: EventListener;
    private onResultsOutputClick: EventListener;
    private onResultsSubmitBtnFocus: EventListener;

    private accessToken: string;

    constructor(componentEl: HTMLFormElement, messagingService: IMessagingService, private appInsights: ILoggingService,
        @inject(ISessionServiceId) private sessionService: ISessionService) {
        super(componentEl, messagingService);

        this.resultsOutput = this.searchSupport.querySelector(".js-search-support-results");
        
        if (!this.resultsOutput) {
            return;
        }

        this.resultTemplate = this.searchSupport.querySelector("[data-template='results-item']");

        this.autoCompleteResultsAnnouncement = this.searchSupport.querySelector(".js-search-results-announcement");

        this.results = [];
        this.resultsOutputItems = [];

        this.onSearchInputFocus = this.onSearchInputFocusHandler.bind(this);
        this.onSearchInputChange = this.onSearchInputChangeHandler.bind(this);
        this.onSearchInputKeyDown = this.onSearchInputKeyDownHandler.bind(this);
        this.onResultsOutputKeyDown = this.onResultsOutputKeyDownHandler.bind(this);
        this.onResultsOutputClick = this.onResultsOutputClickHandler.bind(this);
        this.onResultsSubmitBtnFocus = this.onResultsSubmitBtnFocusHandler.bind(this);

        this.sessionService.StartSession();
        const onSessionStarted$ = this.sessionService.OnSessionStarted$().pipe(first());
        onSessionStarted$.subscribe(async session =>
            await this.StartSession(session)
        );
    }

    private async StartSession(session: SessionDto) {
        this.accessToken = session.accessToken;
        if (this.accessToken == null) {
            this.appInsights.LogMessage('searchSupportAutoComplete:StartSession error: No access token');
        }
        else {
            this.initAutocomplete();
        }
    }

    protected async initAutocomplete() {
        this.searchInput.addEventListener("focus", this.onSearchInputFocus);
        this.searchInput.addEventListener("input", this.debounceInput(this.onSearchInputChange, 250));
        this.searchInput.addEventListener("keydown", this.onSearchInputKeyDown);

        if (this.findResultsBtn) {
            this.findResultsBtn.addEventListener("focus", this.onResultsSubmitBtnFocus);
        }

        this.resultsOutput.addEventListener("keydown", this.onResultsOutputKeyDown);
        this.resultsOutput.addEventListener("click", this.onResultsOutputClick);

        document.addEventListener("click", (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (!target.closest(".js-search-support-results") && !target.closest(".js-search-support-input")) {
                this.hideResults();
            }
        });
    }

    private debounceInput(callback, delay) {
        let timeout;
        return (...args) => {
            const context = this;
            clearTimeout(timeout);
            timeout = setTimeout(() => callback.apply(context, args), delay);
        };
    }

    private onSearchInputFocusHandler() {
        if (this.hasResults()) {
            this.showResults();
        }
    }

    private onSearchInputChangeHandler(e: InputEvent) {
        const target = e.target as HTMLInputElement;
        let searchterm = target.value.trim();

        if (searchterm.length > 2) {

            if (searchterm.length > 100)
            {
                searchterm = searchterm.slice(0,100).trim();
            }
    
            // if there is at least 2 chars, then encode the search term before request.
            searchterm = encodeURIComponent(searchterm);

            this.fetchResults(searchterm)
                .then((response) => {
                    const results = response;
                    if (Array.isArray(results) && results.length > 0) {
                        this.results = results;
                        this.outputResults(this.buildResults(this.results));
                        this.showResults();
                    }
                    else {
                        this.autoCompleteResultsAnnouncement.textContent = 'There are zero options.';
                        this.searchInput.setAttribute("aria-expanded", "false");
                    }
                })
                .catch((error) => {
                    this.appInsights.LogMessage('searchSupportAutoComplete:onSearchInputChangeHandler error: ' + error);
                });
        } else {
            this.resetResults();
        }
    }

    private onSearchInputKeyDownHandler(e: KeyboardEvent) {
        const items = this.resultsOutputItems;
        if (e.key === "ArrowDown" && this.hasResults() && items.length > 0) {
            this.focusResult(items[0]);
            e.preventDefault();
        } else if (e.key === "Escape" || (e.key === "Tab" && e.shiftKey)) {
            this.hideResults();
        }
    }

    private onResultsOutputKeyDownHandler(e: KeyboardEvent) {
        const target = e.target as HTMLElement;
        const items = this.resultsOutputItems;

        switch (e.key) {
            case "Enter":
                e.target.dispatchEvent(new Event("click", { bubbles: true }));
                break;
            case "ArrowDown":
                if (target !== items[items.length - 1]) {
                    this.focusResult(items[items.indexOf(target) + 1]);
                }
                e.preventDefault();
                break;
            case "ArrowUp":
                items.forEach((item) => item.setAttribute("aria-selected", "false"));
                if (target === items[0]) {
                    this.searchInput.focus();
                } else {
                    this.focusResult(items[items.indexOf(target) - 1]);
                    e.preventDefault();
                }
                break;
            case "Escape":
                this.hideResults();
                this.searchInput.focus();
                break;
            case "Tab":
                if (!e.shiftKey) {
                    this.hideResults();
                }
                break;
        }
    }

    private onResultsOutputClickHandler(e: MouseEvent) {
        const target = e.target as HTMLElement;
        const resultItem = target.closest("[role='option']") as HTMLElement;
        this.searchInput.value = resultItem.querySelector("[data-result-title]").innerHTML;
        this.hideResults();

        var url = window.location.origin + "/browse-support/products?keywords=" + this.searchInput.value + "&sortBy=5&pageSize=25&page=1";
        window.location.href = url;
    }

    private onResultsSubmitBtnFocusHandler() {
        this.hideResults();
    }

    private hasResults() {
        return this.results.length > 0;
    }

    private async fetchResults(searchterm: string): Promise<ServiceResponse<string[]>> {
        this.resetResults();

        const headers = new Headers();
        headers.append("Authorization", `Bearer ${this.accessToken}`);
        headers.append("Accept", "application/json");

        const response = await fetch(`/api/service/suggestions?searchTerm=${searchterm}`, { method: "GET", headers: headers });

        if (response.ok) {
            var responseObj = await response.json();
            return responseObj;
        } else {
            const error = new Error("Autocomplete fetch failed");
            return Promise.reject(error);
        }
    }

    private buildResults(results: string[]) {
        if (!this.resultTemplate) {
            return;
        }

        return results.map((result, i) => {
            const clone = this.resultTemplate.content.cloneNode(true) as HTMLElement;
            const container = clone.querySelector("[data-result-container]");

            container.setAttribute("aria-posinset", `${i + 1}`);
            container.setAttribute("aria-setsize", `${results.length}`);

            const title = clone.querySelector("[data-result-title]") as HTMLSpanElement;
            title.innerText = result;

            return clone;
        });
    }

    private outputResults(elements) {
        elements.forEach((node) => {
            this.resultsOutput.appendChild(node);
        });
    }

    private showResults() {
        this.resultsOutputItems = [...this.resultsOutput.querySelectorAll("[role='option']")] as HTMLElement[];
        this.autoCompleteResultsAnnouncement.textContent = this.resultsOutputItems.length === 1 ? "There is 1 option." : `There are ${this.resultsOutputItems.length} options.`;
        this.searchInput.setAttribute("aria-expanded", "true");
    }

    private hideResults() {
        if (this.resultsOutputItems) {
            this.resultsOutputItems.forEach((item) => item.setAttribute("aria-selected", "false"));
        }
        this.searchInput.setAttribute("aria-expanded", "false");
    }

    private resetResults() {
        while (this.resultsOutput.firstChild) {
            this.resultsOutput.removeChild(this.resultsOutput.firstChild);
        }
        this.results = [];
        this.resultsOutputItems = null;
        this.hideResults();
    }

    private focusResult(target) {
        this.resultsOutputItems.forEach((item) => item.setAttribute("aria-selected", "false"));
        target.setAttribute("aria-selected", "true");
        target.focus();
    }
}

export { SearchSupportAutoComplete };
